import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import withRouter from "components/Common/withRouter"
import { Link } from "react-router-dom"

//i18n
import { withTranslation, WithTranslation } from "react-i18next"
import { WithRouterProps } from "components/Common/withRouter"
import { menuData, type MenuItem, type MenuItemChild } from "config/menu"
import { AUTH_USER_UPDATED_EVENT, getStoredAuthUser } from "helpers/auth_api"

interface SidebarContentProps extends WithRouterProps, WithTranslation {
  location?: any
}

const SidebarContent: React.FC<SidebarContentProps> = (props) => {
  const ref = useRef<any>(null);
  const t = (key: string) => (props.t as (k: string) => string)(key);

  const readUserRole = () => getStoredAuthUser()?.role ?? null
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(readUserRole)

  useEffect(() => {
    const refreshRole = () => setCurrentUserRole(readUserRole())
    refreshRole()
    window.addEventListener(AUTH_USER_UPDATED_EVENT, refreshRole)
    return () => window.removeEventListener(AUTH_USER_UPDATED_EVENT, refreshRole)
  }, [])

  const canShowItem = (roles?: string[]) => {
    if (!roles || roles.length === 0) return true
    if (!currentUserRole) return false
    return roles.some((r) => r.trim().toLowerCase() === currentUserRole.trim().toLowerCase())
  }

  const activateParentDropdown = useCallback((item: HTMLAnchorElement) => {
    item.classList.add("active");
    const parent = item.parentElement;
    
    if (parent) {
      const parent2El = parent.childNodes[1] as HTMLElement;
      if (parent2El && parent2El.id !== "side-menu") {
        parent2El.classList.add("mm-show");
      }

      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show"); // ul tag

        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add("mm-active"); // li
          const firstChild = parent3.childNodes[0] as HTMLElement;
          if (firstChild) {
            firstChild.classList.add("mm-active"); //a
          }
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add("mm-show"); // ul
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add("mm-show"); // li
              const firstChild5 = parent5.childNodes[0] as HTMLElement;
              if (firstChild5) {
                firstChild5.classList.add("mm-active"); // a tag
              }
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }, []);

  const removeActivation = (items: HTMLCollectionOf<HTMLAnchorElement>) => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i];
      const parent = items[i].parentElement;

      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.length > 1 && parent.childNodes[1]
            ? (parent.childNodes[1] as HTMLElement)
            : null;
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }

        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove("mm-show");

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active"); // li
            const firstChild = parent3.childNodes[0] as HTMLElement;
            if (firstChild) {
              firstChild.classList.remove("mm-active");
            }

            const parent4 = parent3.parentElement; // ul
            if (parent4) {
              parent4.classList.remove("mm-show"); // ul
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show"); // li
                const firstChild5 = parent5.childNodes[0] as HTMLElement;
                if (firstChild5) {
                  firstChild5.classList.remove("mm-active"); // a tag
                }
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const pathName = process.env.PUBLIC_URL + props.router.location.pathname;
    let matchingMenuItem: HTMLAnchorElement | null = null;
    const ul = document.getElementById("side-menu");
    if (!ul) return;
    const items = ul.getElementsByTagName("a");
    removeActivation(items);

    for (let i = 0; i < items.length; ++i) {
      if (pathName === items[i].pathname) {
        matchingMenuItem = items[i];
        break;
      }
    }
    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [props.router.location.pathname, activateParentDropdown]);

  useEffect(() => {
    if (ref.current) {
      ref.current.recalculate();
    }
  }, []);

  useEffect(() => {
    new MetisMenu("#side-menu");
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    activeMenu();
  }, [activeMenu]);

  function scrollElement(item: HTMLElement | null) {
    if (item && ref.current) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        const scrollElement = ref.current.getScrollElement();
        if (scrollElement) {
          scrollElement.scrollTop = currentPosition - 300;
        }
      }
    }
  }

  const renderBadge = (badge: { text: string; variant?: string }) =>
    badge?.text ? (
      <span className={`badge rounded-pill bg-${badge.variant || "primary"} float-end`}>{badge.text}</span>
    ) : null

  const renderChild = (child: MenuItemChild) => {
    if (!canShowItem(child.roles)) return null
    return (
      <li key={child.key}>
        <Link to={child.url}>
          {child.icon && <i className={child.icon}></i>}
          {child.badge && renderBadge(child.badge)}
          {t(child.label)}
        </Link>
        {child.children && child.children.length > 0 && (
          <ul className="sub-menu">
            {child.children.map((c) => renderChild(c)).filter(Boolean)}
          </ul>
        )}
      </li>
    )
  }

  const renderItem = (item: MenuItem, index: number) => {
    if (item.isTitle) {
      return (
        <li key={item.key || `title-${index}`} className="menu-title">
          {t(item.label)}
        </li>
      )
    }
    const filteredChildren = item.children?.filter((child) => canShowItem(child.roles)) ?? []
    const hasChildren = filteredChildren.length > 0
    const linkTo = hasChildren ? "/#" : (item.url || "#")
    const linkClass = hasChildren ? "has-arrow waves-effect" : "waves-effect"
    if (item.roles && !canShowItem(item.roles)) return null
    return (
      <li key={item.key || index}>
        <Link to={linkTo} className={linkClass}>
          {item.icon && <i className={item.icon}></i>}
          {item.badge && renderBadge(item.badge)}
          <span>{t(item.label)}</span>
        </Link>
        {hasChildren && (
          <ul className="sub-menu">
            {filteredChildren.map((child) => renderChild(child))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <React.Fragment>
      <SimpleBar style={{ maxHeight: "100%" }} ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            {menuData.map(renderItem)}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))
